import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCallback, useRef } from "react";
import { toast } from "sonner";
import { triggerXPToast } from "@/components/leaderboard/XPToast";

const DAILY_QUEST_KEYS = {
  quest: ['daily-quest'] as const,
  completion: (userId: string) => ['daily-quest-completion', userId] as const,
  streak: (userId: string) => ['daily-quest-streak', userId] as const,
};

const STREAK_BONUSES: Record<number, number> = { 7: 50, 14: 150, 30: 500 };

export function useDailyQuest() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const completingRef = useRef(false);

  // Get today's quest task_id
  const questQuery = useQuery({
    queryKey: DAILY_QUEST_KEYS.quest,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_or_create_daily_quest');
      if (error) throw error;
      return data as string | null;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Get the task details
  const taskQuery = useQuery({
    queryKey: ['daily-quest-task', questQuery.data],
    queryFn: async () => {
      if (!questQuery.data) return null;
      const { data, error } = await supabase
        .from('tasks')
        .select('id, question, category, categories, task_type, task_subtype, image_urls, image_url, year, task_number, subpoint, image_thumbnails')
        .eq('id', questQuery.data)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!questQuery.data,
    staleTime: 1000 * 60 * 30,
  });

  // Check if user completed today's quest
  const completionQuery = useQuery({
    queryKey: DAILY_QUEST_KEYS.completion(user?.id || ''),
    queryFn: async () => {
      if (!user) return false;
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('daily_quest_completions')
        .select('id')
        .eq('user_id', user.id)
        .eq('quest_date', today)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2,
  });

  // Get streak
  const streakQuery = useQuery({
    queryKey: DAILY_QUEST_KEYS.streak(user?.id || ''),
    queryFn: async () => {
      if (!user) return 0;
      const { data, error } = await supabase.rpc('get_daily_quest_streak', {
        p_user_id: user.id,
      });
      if (error) throw error;
      return (data as number) || 0;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2,
  });

  // Complete daily quest mutation
  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not logged in');
      const today = new Date().toISOString().split('T')[0];
      const { error } = await supabase
        .from('daily_quest_completions')
        .insert({ user_id: user.id, quest_date: today });
      if (error) {
        if (error.code === '23505') return; // Already completed
        throw error;
      }
    },
    onSuccess: () => {
      if (user) {
        queryClient.invalidateQueries({ queryKey: DAILY_QUEST_KEYS.completion(user.id) });
        queryClient.invalidateQueries({ queryKey: DAILY_QUEST_KEYS.streak(user.id) });
      }
    },
  });

  // Called when any task is answered correctly - checks if it's today's quest
  const checkAndCompleteQuest = useCallback(async (taskId: string) => {
    if (!user || !questQuery.data || completionQuery.data || completingRef.current) return;
    if (taskId !== questQuery.data) return;

    completingRef.current = true;
    try {
      await completeMutation.mutateAsync();

      // Fetch updated streak
      const { data: newStreakData } = await supabase.rpc('get_daily_quest_streak', { p_user_id: user.id });
      const newStreak = (newStreakData as number) || 1;

      // Check milestone bonus
      const streakBonus = STREAK_BONUSES[newStreak] ?? 0;

      // Award milestone bonus XP if applicable
      if (streakBonus > 0) {
        await supabase.rpc('award_xp', { p_amount: streakBonus, p_reason: `daily_streak_${newStreak}` });
      }

      // Invalidate XP & leaderboard so the progress bar updates
      queryClient.invalidateQueries({ queryKey: ['user-xp'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });

      // Animated XP toast (base 25 already granted by record_task_attempt + bonus)
      const totalXP = 25 + streakBonus;
      triggerXPToast(totalXP, "za Daily Quest");

      // Text toast with full info
      if (streakBonus > 0) {
        toast.success(`🎉 Milestone ${newStreak} dni! +${streakBonus} XP bonus!`, {
          duration: 5000,
          position: 'bottom-right',
        });
        setTimeout(() => {
          toast.success(
            `🎯 Daily Quest ukończony! +${totalXP} XP | ${newStreak} ${newStreak === 1 ? 'dzień' : 'dni'} z rzędu`,
            { duration: 4000, position: 'bottom-right' }
          );
        }, 1000);
      } else {
        toast.success(
          `🎯 Daily Quest ukończony! +${totalXP} XP | ${newStreak} ${newStreak === 1 ? 'dzień' : 'dni'} z rzędu`,
          { duration: 4000, position: 'bottom-right' }
        );
      }
    } catch {
      // Silently ignore (already completed etc.)
    } finally {
      completingRef.current = false;
    }
  }, [user, questQuery.data, completionQuery.data, completeMutation, queryClient]);

  return {
    taskId: questQuery.data ?? null,
    task: taskQuery.data ?? null,
    isCompleted: completionQuery.data ?? false,
    streak: streakQuery.data ?? 0,
    isLoading: questQuery.isLoading || taskQuery.isLoading,
    checkAndCompleteQuest,
  };
}
